import { useCallback, useEffect, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../../components/recharts/functions";
import { ReMixChart } from "../../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../../models/chart_model";
// import { renderHourTick } from "../../../../../components/recharts/components";
import loader from "../../../../../components/recharts/loader.gif";
import { fetchOptimizerGeneration } from "../../../../../Rest_api/RestApi";
import PortfolioPiechart from "./PortfolioPiechart";

interface GenerationData {
  isgs: number;
  outage: number;
  re: number;
  sgu: number;
  isgs_percentage: number;
  outage_percentage: number;
  re_percentage: number;
  sgu_percentage: number;
}

export default function GenerationAnalysis({
  selectedBlock,
  refreshKey,
  setError,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
}) {
  const [data, setData] = useState<GenerationData[]>([]);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const chartSuffixes = [
    {
      suffix: "isgs",
      displayName: "ISGS",
      stackSuffix: "isgs",
      stroke: "rgba(99, 159, 203, 1)",
      chartType: ChartType.Bar,
      unit: "MWh",
      yAxisId: "left",
    },
    {
      suffix: "re",
      displayName: "RE",
      stackSuffix: "re",
      stroke: "rgba(255, 136, 148, 1)",
      chartType: ChartType.Bar,
      unit: "MWh",
      yAxisId: "left",
    },
    {
      suffix: "sgu",
      displayName: "SGU",
      stackSuffix: "sgu",
      stroke: "rgba(52, 101, 109, 1)",
      chartType: ChartType.Bar,
      unit: "MWh",
      yAxisId: "left",
    },
    {
      suffix: "outage",
      displayName: "Outage",
      stackSuffix: "outage",
      stroke: "rgba(244, 127, 89, 1)",
      chartType: ChartType.Bar,
      unit: "MWh",
      yAxisId: "left",
    },
  ];

  const [selectedMetrics] = useState<any[]>([
    chartSuffixes[0],
    chartSuffixes[1],
    chartSuffixes[2],
    chartSuffixes[3],
  ]);

  const handleFetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchOptimizerGeneration();
      const response = [res];

      if (Array.isArray(response)) {
        setData(response);
      } else if (
        response &&
        typeof response === "object" &&
        Array.isArray(response)
      ) {
        setData(response);
      } else {
        console.error("Invalid data format received:", response);
        setError("Invalid data format received from API.");
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching data", error);
      setError("An error occurred while fetching data.");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, refreshKey]);

  useEffect(() => {
    if (!Array.isArray(data) || data.length === 0) {
      setChartData([]);
      setChartKeys([]);
      return;
    }

    // Remove date and slot fields from the data and add a category field
    const filteredData = data.map(({ isgs, outage, re, sgu }, index) => ({
      category: "Total", // Single category for all data
      isgs,
      outage,
      re,
      sgu,
    }));

    const par: ChartDataParams = {
      keys: ["category"],
      tableKeys: [],
      groupBy: ["category"],
      volumeKey: ``,
      aggregationKeys: ["isgs", "outage", "re", "sgu"],
      averageKeys: [],
      averageDuplicationKeys: [],
      priceKey: "",
      data: filteredData,
    };

    try {
      const cData = FormatChartData(par);
      setChartData(cData.data);
      setChartKeys(cData.keys);
    } catch (error) {
      console.error("Error formatting chart data:", error);
      setChartData([]);
      setChartKeys([]);
    }
  }, [data]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    yAxisId: string,
    stroke: string,
    legendColor: string
  ) => {
    return {
      dataKey: `${key}_${suffix}`,
      name: displayName, // Simplified to just show the display name
      type: [""].includes(suffix) ? ChartType.Line : ChartType.Bar,
      unit: unit,
      stroke: stroke,
      legendColor: legendColor,
      yAxisId: yAxisId,
    };
  };

  function getLegends() {
    return selectedMetrics.flatMap(
      ({
        suffix,
        displayName,
        stackSuffix,
        chartType,
        unit,
        yAxisId,
        stroke,
        legendColor,
      }) =>
        chartKeys.map(
          (key, index) =>
            createChartObject(
              key,
              index,
              suffix,
              displayName,
              stackSuffix,
              chartType,
              unit,
              yAxisId,
              stroke,
              legendColor
            ) as any
        )
    );
  }

  return (
    <>
      <div className="flex flex-col md:flex-row gap-2 !h-[100%]">
        <div className="dash-card border border-[#718EBF1C] bg-white shadow-[rgba(149,157,165,0.2)_0px_8px_24px] w-full !min-h-[100%] rounded-2xl">
          <div className="flex w-full justify-center items-center mb-1 border-b border-[#DFEAF2] pb-2">
            <h2 className="text-start chartHeading w-full ">
              Generation Summary
            </h2>
          </div>
          {loading ? (
            <div className=" w-full bg-white flex items-center justify-center">
              <img
                src={loader}
                width={200}
                alt="loader"
                className="block mt-[5rem]"
              />
            </div>
          ) : (
            <>
              <ReMixChart
                chartHeight="calc(100% - 50px)"
                data={chartData}
                showBrush={false}
                brushStart={BrushStart.Start}
                isTimeSlot={false}
                onlyTitle={false}
                legends={getLegends()}
                yAxisLabel="MWh"
                nameGap={40}
                xDataKey="category"
                top={10}
                bottom={30}
                showToolbox={false}
              />
            </>
          )}
        </div>
        <div className="dash-card border border-[#718EBF1C] bg-white shadow-[rgba(149,157,165,0.2)_0px_8px_24px] w-full !min-h-[100%] rounded-2xl">
          <div className="flex w-full justify-center items-center border-b border-[#DFEAF2] ">
            <h2 className="text-start chartHeading w-full ">
              Generation Portfolio
            </h2>
          </div>
          {loading ? (
            <div className=" w-full bg-white flex items-center justify-center">
              <img
                src={loader}
                width={200}
                alt="loader"
                className="block mt-[5rem]"
              />
            </div>
          ) : (
            
              <PortfolioPiechart data={data} />
          
          )}
        </div>
      </div>
    </>
  );
}
